import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import Button, { ButtonVariant } from '../deprecated-button/button';
import Link from '../link/link';
import RIcon, { type RIconComponent } from '../remix-icon';
import Typography from '../typography/typography';

interface EmptyStateProps {
  title?: string;
  info?: string;
  linkText?: string;
  linkHref?: string;
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  icon?: RIconComponent;
  onClick?: () => void;
  hasBorder?: boolean;
  className?: string;
}

const EmptyState = ({
  icon,
  title,
  info,
  linkText,
  linkHref,
  buttonText,
  buttonVariant = ButtonVariant.primary,
  hasBorder = true,
  onClick,
  className,
}: EmptyStateProps) => {
  const theme = useTheme();

  return (
    <Wrapper $hasBorder={hasBorder} className={className}>
      {icon && (
        <IconContainer>
          <RIcon icon={icon} color={theme.palette.secondary.main} size="lg" />
        </IconContainer>
      )}
      <InfoContainer>
        <Typography variant={TypographyVariant.bodyLg2}>{title}</Typography>
        <InfoTypography
          variant={TypographyVariant.bodyMd1}
          color={theme.palette.neutral[90]}
        >
          {info}
        </InfoTypography>
      </InfoContainer>
      {linkText && linkHref && (
        <Link label={linkText} isExternalLink href={linkHref} />
      )}
      {buttonText && onClick && (
        <Button variant={buttonVariant} label={buttonText} onClick={onClick} />
      )}
    </Wrapper>
  );
};

export default memo(EmptyState);

const Wrapper = styled.div<{ $hasBorder: boolean }>`
  ${({ theme, $hasBorder }) => css`
    width: 100%;
    border-radius: ${theme.borderRadius.sm};
    border: ${$hasBorder ? `1px solid ${theme.palette.neutral[60]}` : 'none'};
    padding: ${theme.spacing(3)};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(2.5)};
  `}
`;

const InfoContainer = styled.div`
  ${({ theme }) => css`
    text-align: center;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `}
`;

const IconContainer = styled.div`
  height: 32px;
`;

const InfoTypography = styled(Typography)`
  white-space: pre-line;
`;
